export interface CategoryDraftPayload {
  name: string;
  description: string;
  detailedDescription?: string;
  tips?: string;
  example?: string;
  wordDetailedDescription?: string;
  tags?: string;
  sortOrder?: number;
  includeInTasks?: boolean;
  isEnabled?: boolean;
  approvalStatus?: string;
}

const extractJsonCandidate = (raw: string): string | null => {
  const trimmed = raw.trim();
  if (!trimmed) {
    return null;
  }

  const fencedMatch = trimmed.match(/```(?:json)?\s*([\s\S]*?)\s*```/i);
  if (fencedMatch?.[1]) {
    return fencedMatch[1].trim();
  }

  if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
    return trimmed;
  }

  const arrayMatch = trimmed.match(/\[\s*\{[\s\S]*\}\s*\]/);
  if (arrayMatch?.[0]) {
    return arrayMatch[0];
  }

  return null;
};

export const parseCategoryDrafts = (raw: string): CategoryDraftPayload[] => {
  const candidate = extractJsonCandidate(raw);
  if (!candidate) {
    return [];
  }

  try {
    const parsed = JSON.parse(candidate);
    if (Array.isArray(parsed)) {
      return parsed
        .filter((item): item is Record<string, unknown> => Boolean(item) && typeof item === 'object')
        .map((item) => {
          const draft: CategoryDraftPayload = {
            name: String(item.name ?? item.Name ?? '').trim(),
            description: String(item.description ?? item.Description ?? '').trim(),
          };

          const detailedDescription = typeof item.detailedDescription === 'string'
            ? item.detailedDescription
            : typeof item.DetailedDescription === 'string'
              ? item.DetailedDescription
              : undefined;
          if (detailedDescription) {
            draft.detailedDescription = detailedDescription;
          }

          const tips = typeof item.tips === 'string'
            ? item.tips
            : typeof item.Tips === 'string'
              ? item.Tips
              : undefined;
          if (tips) {
            draft.tips = tips;
          }

          const example = typeof item.example === 'string'
            ? item.example
            : typeof item.Example === 'string'
              ? item.Example
              : undefined;
          if (example) {
            draft.example = example;
          }

          const wordDetailedDescription = typeof item.wordDetailedDescription === 'string'
            ? item.wordDetailedDescription
            : typeof item.WordDetailedDescription === 'string'
              ? item.WordDetailedDescription
              : undefined;
          if (wordDetailedDescription) {
            draft.wordDetailedDescription = wordDetailedDescription;
          }

          const tags = typeof item.tags === 'string'
            ? item.tags
            : typeof item.Tags === 'string'
              ? item.Tags
              : undefined;
          if (tags) {
            draft.tags = tags;
          }

          if (typeof item.sortOrder === 'number') {
            draft.sortOrder = item.sortOrder;
          }

          const approvalStatus = typeof item.approvalStatus === 'string'
            ? item.approvalStatus
            : typeof item.ApprovalStatus === 'string'
              ? item.ApprovalStatus
              : undefined;
          if (approvalStatus) {
            draft.approvalStatus = approvalStatus;
          }

          return draft;
        })
        .filter((item) => item.name || item.description);
    }
  } catch {
    return [];
  }

  return [];
};
